import type { FC } from 'react';
import React from 'react';
import { Aligner } from '@alfa-bank/site-aligner';
import { useSelector } from 'react-redux';
import cn from 'classnames';
import { PlateDesktop as Plate } from '@alfa-bank/site-plate/desktop';
import { ButtonDesktop as Button } from '@alfa-bank/site-button-v2/desktop';
// TODO: https://jira.moscow.alfaintra.net/browse/ALFABANKRU-83779
// eslint-disable-next-line package-imports/no-core-components-imports
import { Badge } from '@alfalab/core-components-badge';
import InformationCircleMIcon from '@alfalab/icons-glyph/InformationCircleMIcon';
import {
    selectFinishData,
    selectAppData,
    selectBasketData,
} from '@alfa-bank/site-dc-form-api/store';

import type { IDesktopProps, IDesktopText, IMobileProps, IMobileText, TThanksPage } from './models';
import { adaptDateTime, getTitleByStatus } from './helpers';

export const ThanksPage: FC<TThanksPage> = ({
    InfoBlock,
    OnlineBlock,
    Text,
    children,
    isMobile = false,
    styles,
    thanksPage,
}) => {
    const {
        additionalInfoVariation,
        deliveryAddress = '',
        deliveryTypeStatus,
        selectedTimeSlot,
    } = useSelector(selectFinishData);
    const { userStatus } = useSelector(selectAppData);
    const { services } = useSelector(selectBasketData);

    const status = userStatus?.find(
        (item): item is 'DT' | 'NODT' => item === 'DT' || item === 'NODT'
    );

    const deliveryDate = selectedTimeSlot ? adaptDateTime(selectedTimeSlot) : null;
    const finalStatus = status || 'NODT';
    const { anyOfficeLink, statusTitles, text } = thanksPage[finalStatus].default;

    return (
        <div data-test-id="dc-form-thanks-page">
            <Aligner align={isMobile ? 'left' : 'center'}>
                <Text
                    className={cn(styles.title, styles.external)}
                    styler={{
                        marginTop: 0,
                        marginBottom: 24,
                    }}
                    tag="h3"
                    view={isMobile ? 'medium' : 'large'}
                >
                    {getTitleByStatus(deliveryTypeStatus, statusTitles)}
                </Text>
                <InfoBlock
                    additionalInfoVariations={additionalInfoVariation}
                    anyOfficeLink={anyOfficeLink}
                    deliveryAddress={deliveryAddress}
                    deliveryDate={deliveryDate}
                    services={services}
                    status={deliveryTypeStatus}
                    text={text as IDesktopText & IMobileText}
                >
                    {children}
                </InfoBlock>
                {additionalInfoVariation === 'childrenBrokerage' && text.brokerageConfig && (
                    <Aligner
                        align="left"
                        styler={{
                            marginLeft: isMobile ? 24 : 96,
                            marginRight: isMobile ? 24 : 96,
                            marginBottom: isMobile ? 24 : 40,
                        }}
                    >
                        <Plate
                            background="#E4F0FF"
                            border
                            borderColor="#2288FA"
                            leftAddons={
                                <Badge
                                    content={<InformationCircleMIcon />}
                                    iconColor="link"
                                    view="icon"
                                />
                            }
                            shadow={false}
                            view="custom"
                        >
                            <Text styler={{ margin: 0 }} view="primary-small">
                                {text.brokerageConfig.text}
                            </Text>
                            <Button
                                href={text.brokerageConfig.downloadLink}
                                size="xxs"
                                styler={{ marginTop: 8 }}
                            >
                                {text.brokerageConfig.downloadButtonText}
                            </Button>
                        </Plate>
                    </Aligner>
                )}
                <OnlineBlock
                    {...(thanksPage[finalStatus].default as IMobileProps & IDesktopProps)}
                    text={text as IDesktopText & IMobileText}
                >
                    {children}
                </OnlineBlock>
            </Aligner>
        </div>
    );
};
